import "../header/header.css";
import logo from "../../assets/images/logo.jpg";
import {
  Search,
  MapPinned,
  User,
  Heart,
  LogOut,
  X,
} from "lucide-react";
import { ShoppingCart } from "lucide-react";
import { Link } from 'react-router-dom';
import "bootstrap/dist/css/bootstrap.min.css";
import Select from "../selectdropdown/Select";
import { useEffect, useRef, useState, useContext } from "react";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Button from "@mui/material/Button";
import Nav from "./nav/Nav";
import { MyContext } from "../../../src/App";
import { useNavigate } from "react-router-dom";
import { fetchDataFromApi } from "../../utils/api";


const Header = () => {
  const [isOpenDropdown, setisOpenDropdown] = useState(false);
  const headerRef = useRef();
  const [headerCategories, setHeaderCategories] = useState([]);
  const [watchlistCount, setWatchlistCount] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Enhanced search states
  const [searchQuery, setSearchQuery] = useState("");
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearchLoading, setIsSearchLoading] = useState(false);
  const [recentSearches, setRecentSearches] = useState([]);

  const searchTimeout = useRef(null);
  const searchRef = useRef(null);
  const navigate = useNavigate();

  const context = useContext(MyContext);
  const {
    isLogin,
    setIsLoging,
    categories: appCategories,
    loadingCategories,
    cartItems,
    getCartData,
    countryList,
    watchlistItems,
    getWatchlistData,
    setAlertBox,
  } = context;

  useEffect(() => {
    const stored = localStorage.getItem("recentSearches");
    if (stored) {
      try {
        setRecentSearches(JSON.parse(stored));
      } catch (e) {
        console.error("Error parsing recent searches:", e);
      }
    }
  }, []);

  useEffect(() => {
    if (!loadingCategories && appCategories.length > 0) {
      setHeaderCategories(appCategories);
    }
  }, [appCategories, loadingCategories]);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && getCartData) {
      getCartData();
    }
    if (token && getWatchlistData) {
      getWatchlistData();
    }
  }, [getCartData, getWatchlistData, isLogin]);

  useEffect(() => {
    if (watchlistItems && Array.isArray(watchlistItems)) {
      setWatchlistCount(watchlistItems.length);
    }
  }, [watchlistItems]);

  const fetchSearchSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setSearchSuggestions([]);
      return;
    }
    setIsSearchLoading(true);
    try {
      const response = await fetchDataFromApi(
        `/api/search/suggestions?q=${encodeURIComponent(query)}`
      );
      if (response?.success) {
        setSearchSuggestions(response.data.suggestions || []);
      }
    } catch (error) {
      console.error("Error fetching suggestions:", error);
      setSearchSuggestions([]);
    } finally {
      setIsSearchLoading(false);
    }
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);

    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    searchTimeout.current = setTimeout(() => {
      fetchSearchSuggestions(value);
    }, 300);

    setShowSuggestions(true);
  };

  const handleSearch = (query = null) => {
    const searchTerm = query || searchQuery.trim();

    if (!searchTerm) return;

    const updatedRecent = [
      searchTerm,
      ...recentSearches.filter((term) => term !== searchTerm),
    ].slice(0, 10);

    setRecentSearches(updatedRecent);
    localStorage.setItem("recentSearches", JSON.stringify(updatedRecent));

    let searchUrl = `/search?q=${encodeURIComponent(searchTerm)}`;
    if (selectedCategory && selectedCategory._id) {
      searchUrl += `&cat=${selectedCategory._id}`;
    }

    navigate(searchUrl);
    setShowSuggestions(false);
    setSearchQuery(searchTerm);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
    if (e.key === "Escape") {
      setShowSuggestions(false);
    }
  };

  const clearSearch = () => {
    setSearchQuery("");
    setSearchSuggestions([]);
    setShowSuggestions(false);
  };

  const removeRecentSearch = (termToRemove) => {
    const updated = recentSearches.filter((term) => term !== termToRemove);
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  // --- THIS IS THE CORRECTED FUNCTION ---
  const handleCategorySelect = (category) => {
    console.log('Selected category:', category);
    setSelectedCategory(category); // Keep this to update the placeholder text

    // Check if a specific category was selected (i.e., it has an _id)
    if (category && category._id) {
      // A specific category was chosen, so create URL parameters
      const queryParams = new URLSearchParams({
        category: category._id,
        name: category.name
      }).toString();
      
      // Navigate to the listing page with the filter
      navigate(`/listing?${queryParams}`);
    } else {
      // "All Categories" was chosen, so navigate to the listing page without any filters
      navigate('/listing');
    }
  };

  const getCategorySelectData = () => {
    if (!headerCategories || headerCategories.length === 0) {
      return [];
    }
    
    const categoryOptions = [
      { 
        _id: null, 
        name: "All Categories",
        color: "#666"
      },
      ...headerCategories.map(cat => ({
        _id: cat._id,
        name: cat.name,
        color: cat.color || "#666"
      }))
    ];
    
    return categoryOptions;
  };

  const handleSignOut = () => {
    try {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      localStorage.removeItem("recentSearches");

      setIsLoging(false);

      context.setAlertBox({
        open: true,
        error: false,
        msg: "User logged out successfully!"
      });

      setisOpenDropdown(false);

      setTimeout(() => {
        navigate("/login");
      }, 500);
    } catch (error) {
      console.error("Error during logout:", error);
      context.setAlertBox({
        open: true,
        error: true,
        msg: "Error occurred during logout. Please try again."
      });
    }
  };

  const handleCartClick = () => {
    navigate("/cart");
  };

  const handleWishlistClick = () => {
    navigate("/watchlist");
  };

  const handleOrderTracking = () => {
    navigate("/orders");
    setisOpenDropdown(false);
  };

  const handleMyAccount = () => {
    navigate("/my-account");
    setisOpenDropdown(false);
  };

  const handleWishlist = () => {
    navigate("/watchlist");
    setisOpenDropdown(false);
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        headerRef.current.classList.add("fixed");
      } else {
        headerRef.current.classList.remove("fixed");
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getCartItemsCount = () => {
    if (!cartItems || !Array.isArray(cartItems)) return 0;
    return cartItems.reduce((total, item) => total + (item.quantity || 0), 0);
  };

  return (
    <>
      <div className="headerWrapper">
        <header ref={headerRef}>
          <div className="container-fluid">
            <div className="row">
        
              <div className="col-sm-1 logo-wrapper">
                <Link to="/" className="d-flex align-items-center text-decoration-none">
                  <img src={logo} alt="BoxDekho Logo" className="logo-img" />
                  <span className="app-name">
                    <strong className="text-dark">BoxDekho</strong>
                  </span>
                </Link>
              </div>

              <div className="col-sm-6 d-flex">
                <div className="header-Search d-flex align-items-center">
                  <div className="search w-100" ref={searchRef}>
                    <input
                      type="text"
                      placeholder="Search for products, brands, categories..."
                      value={searchQuery}
                      onChange={handleSearchInputChange}
                      onKeyPress={handleSearchKeyPress}
                      onFocus={() => setShowSuggestions(true)}
                    />
                    {searchQuery && (
                      <X
                        className="search-clear cursor"
                        onClick={clearSearch}
                        size={18}
                        style={{
                          position: "absolute",
                          right: "50px",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: "#666",
                          cursor: "pointer",
                        }}
                      />
                    )}
                    <Search
                      className="search-icon cursor"
                      onClick={() => handleSearch()}
                    />
                    {showSuggestions && (
                      <div className="search-suggestions-dropdown">
                        {isSearchLoading && (
                          <div className="suggestion-item loading">
                            <span>Searching...</span>
                          </div>
                        )}
                        {searchSuggestions.length > 0 && (
                          <div className="suggestions-section">
                            <div className="section-title">Suggestions</div>
                            {searchSuggestions.map((suggestion, index) => (
                              <div
                                key={`suggestion-${index}`}
                                className="suggestion-item"
                                onClick={() => handleSearch(suggestion)}
                              >
                                <Search size={14} />
                                <span>{suggestion}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {!isSearchLoading &&
                          searchQuery.length < 2 &&
                          recentSearches.length > 0 && (
                            <div className="suggestions-section">
                              <div className="section-title">
                                Recent Searches
                              </div>
                              {recentSearches
                                .slice(0, 5)
                                .map((recent, index) => (
                                  <div
                                    key={`recent-${index}`}
                                    className="suggestion-item recent-item"
                                  >
                                    <div
                                      onClick={() => handleSearch(recent)}
                                      className="recent-content"
                                    >
                                      <Search size={15} />
                                      <span>{recent}</span>
                                    </div>
                                    <X
                                      size={12}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        removeRecentSearch(recent);
                                      }}
                                      className="remove-recent"
                                    />
                                  </div>
                                ))}
                            </div>
                          )}
                        {!isSearchLoading &&
                          searchQuery.length >= 2 &&
                          searchSuggestions.length === 0 && (
                            <div className="suggestion-item no-results">
                              No suggestions found for "{searchQuery}"
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-sm-5 d-flex align-items-center country">
                <div className="ml-auto d-flex align-items-center">
                  {/* <div className="countryWrapper">
                    <Select
                      className="countryselet"
                      data={countryList}
                      placeholder={"Your location"}
                      icon={<MapPin style={{ opacity: "0.5" }} />}
                    />
                  </div> */}
                  <ClickAwayListener
                    onClickAway={() => setisOpenDropdown(false)}
                  >
                    <ul className="list list-inline mb-0 header-Tabs">
                      <li className="list-inline-item">
                        <span
                          className="compare-wrapper"
                          onClick={handleWishlistClick}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="icon-wrapper">
                            <Heart />
                            <span className="compare-badge bg-success rounded-circle">
                              {watchlistCount}
                            </span>
                          </span>
                          <span className="compare-text">Wishlist</span>
                        </span>
                      </li>
                      <li className="list-inline-item">
                        <span
                          className="compare-wrapper"
                          onClick={handleCartClick}
                          style={{ cursor: "pointer" }}
                        >
                          <span className="icon-wrapper">
                            <ShoppingCart />
                            <span className="compare-badge bg-success rounded-circle">
                              {getCartItemsCount()}
                            </span>
                          </span>
                          <span className="compare-text">Cart</span>
                        </span>
                      </li>
                      <li className="list-inline-item">
                        {isLogin ? (
                          <span
                            className="compare-wrapper"
                            onClick={() => setisOpenDropdown(!isOpenDropdown)}
                            style={{ cursor: "pointer" }}
                          >
                            <span className="icon-wrapper">
                              <User />
                            </span>
                            <span className="compare-text">Account</span>
                            
                            {isOpenDropdown && (
                              <ul className="dropdownmenu">

                                <li>
                                  <Button onClick={handleOrderTracking}>
                                    <MapPinned /> Order Tracking
                                  </Button>
                                </li>
                                <li>
                                  <Button onClick={handleWishlist}>
                                    <Heart /> My Wishlist
                                  </Button>
                                </li>
                                <li>
                                  <Button onClick={handleSignOut}>
                                    <LogOut /> Sign Out
                                  </Button>
                                </li>
                              </ul>
                            )}
                          </span>
                        ) : (
                          <Button
                            onClick={() => navigate("/signup")}
                            style={{
                              backgroundColor: "white",
                              color: "green",
                              border: "1px solid green",
                              padding: "8px 20px",
                              borderRadius: "20px",
                              fontWeight: "600",
                              fontSize: "14px",
                            }}
                          >
                            Sign Up
                          </Button>
                        )}
                      </li>
                    </ul>
                  </ClickAwayListener>
                </div>
              </div>
            </div>
          </div>
        </header>
        <Nav />
      </div>
    </>
  );
};

export default Header;